import { Box, Center, Flex, FlexProps, StackProps } from '@chakra-ui/react'
import { useAuth, useScreen } from '@hb/react'
import { FieldMapValue, User, WithId } from '@hb/shared'
import { FormApi } from 'final-form'
import { PropsWithChildren, useMemo } from 'react'
import { Loading } from '../Loading'
import { SignInToView } from '../Users/SignInToView'
import { PageContainer } from '../Views'
import { SimpleForm } from '../forms'
import { RenderStageProps } from '../forms/renderStage/types'
import { DesktopGynStageList } from './DesktopGynStageList'
import { MedicationOrSupplementModal } from './MedicationOrSupplementModal'
import { MobileGynStageList } from './MobileGynStageList'
import { GynCareViewProvider, useGynCareView } from './context'
import { renderGynStage } from './renderStage'
import { getGynStoragePath } from './utils'

const pageContentProps: FlexProps = {
  pt: 4,
  pb: 2,
  gap: 2,
  maxW: '100%',
}

const GynFormBodyContainer = ({ children }: PropsWithChildren) => (
  <Flex flexFlow="column" w="100%" flex={1} py={2} px={5} minH="0" overflowY="auto">
    {children}
  </Flex>
)
const gynFormBoxProps: StackProps = {
  maxH: '100%',
  bg: 'gray.50',
  pt: 0,
  pb: 0,
  px: 0,
}
// const StageFormRegistrar = () => {
//   const form = useForm()
//   const { registerStageForm, updateStageDirty } = useGynCareView()
//   const { dirty } = useFormState({ subscription: { dirty: true } })
//   useLayoutEffect(() => {
//     updateStageDirty(!!dirty)
//   }, [dirty, updateStageDirty])
//   useEffect(() => {
//     registerStageForm(form)
//     return () => registerStageForm(null)
//   }, [form, registerStageForm])
//   return null
// }

const GynPatientForm = () => {
  const { formData, patient, stage, registerStageForm, onStageSubmit, goNextStage } =
    useGynCareView()
  const data = useMemo(() => formData?.[stage.id], [formData, stage])

  const RenderStage = useMemo(() => renderGynStage[stage.id] ?? null, [stage.id])
  const renderStageProps: RenderStageProps = useMemo(
    () => ({
      onSubmit: onStageSubmit,
      assessmentId: null,
      onComplete: goNextStage,
      patientId: patient.id,
      data: data ?? {},
      readOnly: false,
      storagePath: getGynStoragePath(patient.id, stage.id),
      registerForm: (form: FormApi | null) => registerStageForm(form, stage.id),
    }),
    [onStageSubmit, patient.id, data, stage, goNextStage, registerStageForm],
  )

  const body = RenderStage ? (
    <Box w="100%" height="100%" overflowY="auto">
      <RenderStage {...renderStageProps} />
    </Box>
  ) : (
    <SimpleForm
      boxProps={gynFormBoxProps}
      canSubmitClean
      value={formData?.[stage.id]}
      BodyContainer={GynFormBodyContainer}
      submitCleanText="SAVE"
      submitText="SAVE"
      field={stage}
      onSubmit={onStageSubmit}
      registerForm={(form: FormApi | null) => registerStageForm(form, stage.id)}
    />
  )

  return (
    <Box maxW={stage.id === 'gynAndSexualHistory' ? '1000px' : '800px'} w="100%" flex={1} minH="0">
      {body}
      <MedicationOrSupplementModal />
    </Box>
  )
}

const GYNPatientViewContent = ({ user }: { user: WithId<User> }) => {
  const { width: screenWidth } = useScreen()

  return (
    <GynCareViewProvider user={user} admin={false} canChangePractice={false}>
      {screenWidth < 768 ? <MobileGynStageList /> : <DesktopGynStageList width={screenWidth} />}
      <GynPatientForm />
    </GynCareViewProvider>
  )
}

export const GynCodeFormContent = ({
  user,
  onSubmitOverride,
}: {
  user: WithId<User>
  onSubmitOverride: (data: FieldMapValue) => Promise<{ error?: string; success?: string }>
}) => {
  const { width: screenWidth } = useScreen()

  return (
    <GynCareViewProvider
      user={user}
      admin={false}
      canChangePractice={false}
      onSubmitOverride={onSubmitOverride}>
      {screenWidth < 768 ? <MobileGynStageList /> : <DesktopGynStageList width={screenWidth} />}
      <GynPatientForm />
    </GynCareViewProvider>
  )
}

export const GYNPatientView = () => {
  const { user, authUser, loading } = useAuth()
  if (!authUser && !loading) {
    return <SignInToView message="Sign in to get started" />
  }
  return (
    <PageContainer fullHeight fullWidth contentProps={pageContentProps}>
      {user ? (
        <GYNPatientViewContent user={user} />
      ) : (
        <Center w="100%" h="100%">
          <Loading text="Loading GYN care..." />
        </Center>
      )}
    </PageContainer>
  )
}
